import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/context/I18nContext";
import { rememberEmail } from "@/context/CartContext";

const DISMISS_KEY = "brunchi_newsletter_dismissed";
const SIGNUP_KEY = "brunchi_newsletter_signed";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const { t } = useT();

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) || localStorage.getItem(SIGNUP_KEY)) return;
    const timer = setTimeout(() => setOpen(true), 12000);
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, name });
      rememberEmail(email, name);
      setCode(data.code);
      localStorage.setItem(SIGNUP_KEY, data.code);
      toast.success(t("newsletter.welcome"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("newsletter.failed"));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  // Non-blocking bottom-right card. No full-screen overlay so header/cart remain clickable.
  return (
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-40 max-w-md sm:max-w-sm"
      data-testid="newsletter-popup"
      role="dialog"
      aria-label={t("newsletter.title")}
    >
      <div className="relative bg-[color:var(--brunchi-paper)] rounded-2xl p-5 sm:p-6 shadow-2xl border border-[color:var(--brunchi-stone)] animate-in slide-in-from-bottom-4">
        <button
          onClick={close}
          className="absolute top-2.5 right-2.5 p-1.5 rounded-full hover:bg-[color:var(--brunchi-cream-2)]"
          data-testid="newsletter-close-btn"
          aria-label={t("newsletter.dismiss")}
        >
          <X size={16} />
        </button>
        {code ? (
          <div className="text-center py-1">
            <Sparkles className="mx-auto text-[color:var(--brunchi-green)]" size={32} />
            <div className="font-display text-xl font-semibold mt-2">{t("newsletter.youre_in")}</div>
            <p className="text-xs text-[color:var(--brunchi-ink-soft)] mt-1.5">{t("newsletter.code_use")}</p>
            <div className="mt-3 mx-auto inline-block bg-[color:var(--brunchi-green-light)] border border-dashed border-[color:var(--brunchi-green)] rounded-lg px-5 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--brunchi-green-hover)]">{t("newsletter.your_code")}</div>
              <div className="font-mono text-xl font-bold tracking-wider" data-testid="newsletter-code">{code}</div>
            </div>
            <button onClick={close} className="mt-4 px-5 py-2 rounded-full pill-primary text-sm">
              {t("newsletter.start_shopping")}
            </button>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--brunchi-green-hover)] mb-1.5">
              {t("newsletter.eyebrow")}
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold leading-tight">
              {t("newsletter.title")}
            </h2>
            <p className="text-xs text-[color:var(--brunchi-ink-soft)] mt-1.5">
              {t("newsletter.subtitle")}
            </p>
            <form onSubmit={submit} className="mt-3 space-y-2" data-testid="newsletter-form">
              <input
                type="text"
                placeholder={t("newsletter.name_ph")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm"
                data-testid="newsletter-name"
              />
              <input
                type="email"
                required
                placeholder={t("newsletter.email_ph")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm"
                data-testid="newsletter-email"
              />
              <button
                disabled={busy}
                type="submit"
                className="w-full py-2.5 rounded-full pill-primary font-medium text-sm disabled:opacity-40"
                data-testid="newsletter-submit-btn"
              >
                {busy ? t("newsletter.sending") : t("newsletter.submit")}
              </button>
            </form>
            <p className="text-[10px] text-[color:var(--brunchi-ink-soft)] mt-2 text-center">
              {t("newsletter.terms")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
